import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WorkflowService } from '../../core/services/workflow.service';
import { UserService } from '../../core/services/user.service';
import { ToastService } from '../../core/services/toast.service';
import { AuthService } from '../../core/services/auth.service';
import {
  WorkflowResponse, WorkflowStageResponse, CreateWorkflowRequest,
  CreateWorkflowStageRequest, AssignStageUserRequest, UserProfileResponse
} from '../../core/models/models';
import { ModalComponent } from '../../shared/components/modal/modal.component';

@Component({
  selector: 'app-workflows',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  template: `
    <div class="space-y-6 font-sans">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-extrabold text-slate-900 tracking-tight">Workflow & Approval Stage Engine</h1>
          <p class="text-xs text-slate-500">Configure multi-stage approval pipelines and assign reviewing officers for consent authorizations.</p>
        </div>

        @if (authService.hasPermission('can manage workflows') || authService.hasRole('SuperAdmin')) {
          <button
            (click)="showCreateWfModal.set(true)"
            class="inline-flex items-center gap-2 py-2.5 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-md shadow-emerald-600/20 transition-all active:scale-95"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Create New Workflow
          </button>
        }
      </div>

      <!-- Workflows List Cards -->
      <div class="space-y-6">
        @for (wf of workflows(); track wf.uid) {
          <div class="glass-panel p-6 rounded-3xl border border-slate-200 bg-white space-y-6 shadow-sm">
            <!-- Header Bar -->
            <div class="flex items-center justify-between border-b border-slate-200 pb-4">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold flex items-center justify-center shadow-2xs">
                  WF
                </div>
                <div>
                  <h3 class="text-base font-bold text-slate-900">{{ wf.name }}</h3>
                  <span class="text-xs font-mono text-emerald-700 font-bold">{{ wf.code }}</span>
                </div>
              </div>

              @if (authService.hasPermission('can manage workflows') || authService.hasRole('SuperAdmin')) {
                <button
                  (click)="openAddStageModal(wf.uid)"
                  class="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 font-bold text-xs flex items-center gap-1.5 transition"
                >
                  + Add Stage
                </button>
              }
            </div>

            <!-- Stages Flow Diagram -->
            <div class="space-y-4">
              <span class="text-xs font-bold text-slate-500 uppercase tracking-wider block">Approval Stages Pipeline</span>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 relative">
                @for (stage of wf.stages; track stage.uid) {
                  <div class="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3 relative group hover:border-emerald-300 transition-colors shadow-2xs">
                    <div class="flex items-center justify-between">
                      <span class="w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 font-mono text-xs font-bold flex items-center justify-center border border-emerald-300">
                        {{ stage.stageOrder }}
                      </span>
                      <span class="text-[10px] font-mono text-slate-400">{{ stage.uid }}</span>
                    </div>

                    <div class="font-bold text-slate-900 text-xs">{{ stage.name }}</div>

                    <div class="pt-2 border-t border-slate-200 text-[11px] space-y-1">
                      <div class="text-slate-500 font-medium">Assigned Reviewers:</div>
                      <div class="flex flex-wrap gap-1">
                        @for (usr of stage.assignedUsers; track usr) {
                          <span class="px-2 py-0.5 rounded bg-white border border-slate-300 text-slate-800 font-semibold shadow-2xs">
                            {{ usr }}
                          </span>
                        } @empty {
                          <span class="text-amber-700 font-bold italic">No reviewers assigned</span>
                        }
                      </div>
                    </div>

                    <div class="pt-2 flex justify-end">
                      <button
                        (click)="openAssignUserModal(stage)"
                        class="text-[11px] font-bold text-emerald-700 hover:underline"
                      >
                        + Assign Officer
                      </button>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Create Workflow Modal -->
      <app-modal
        [isOpen]="showCreateWfModal()"
        title="Define Approval Workflow"
        (close)="showCreateWfModal.set(false)"
      >
        <form (ngSubmit)="onCreateWorkflow()" class="space-y-4">
          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Workflow Name</label>
            <input
              type="text"
              [(ngModel)]="newWf.name"
              name="name"
              required
              placeholder="e.g. Financial Sector Data Interoperability Workflow"
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Workflow Unique Code</label>
            <input
              type="text"
              [(ngModel)]="newWf.code"
              name="code"
              required
              placeholder="e.g. FIN-DATA-WF-V1"
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-mono"
            />
          </div>

          <div class="flex justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              (click)="showCreateWfModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-sm"
            >
              Create Workflow
            </button>
          </div>
        </form>
      </app-modal>

      <!-- Add Stage Modal -->
      <app-modal
        [isOpen]="showAddStageModal()"
        title="Add Stage to Workflow"
        (close)="showAddStageModal.set(false)"
      >
        <form (ngSubmit)="onCreateStage()" class="space-y-4">
          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Stage Title</label>
            <input
              type="text"
              [(ngModel)]="newStage.name"
              name="name"
              required
              placeholder="e.g. Stage 2: Financial Compliance Review"
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Stage Sequence Order</label>
            <input
              type="number"
              [(ngModel)]="newStage.stageOrder"
              name="stageOrder"
              required
              min="1"
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-mono"
            />
          </div>

          <div class="flex justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              (click)="showAddStageModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-sm"
            >
              Add Stage
            </button>
          </div>
        </form>
      </app-modal>

      <!-- Assign User to Stage Modal -->
      <app-modal
        [isOpen]="showAssignModal()"
        [title]="'Assign Review Officer to Stage: ' + (selectedStage()?.name || '')"
        (close)="showAssignModal.set(false)"
      >
        <div class="space-y-4">
          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Select Reviewing Officer</label>
            <select
              [(ngModel)]="assignUserUid"
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs"
            >
              @for (usr of allUsers(); track usr.uid) {
                <option [value]="usr.uid">{{ usr.firstName }} {{ usr.lastName }} ({{ usr.email }}) - {{ usr.institutionName }}</option>
              }
            </select>
          </div>

          <div class="flex justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              (click)="showAssignModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300"
            >
              Cancel
            </button>
            <button
              (click)="onAssignUser()"
              class="px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-sm"
            >
              Assign Officer
            </button>
          </div>
        </div>
      </app-modal>
    </div>
  `
})
export class WorkflowsComponent implements OnInit {
  workflowService = inject(WorkflowService);
  userService = inject(UserService);
  toastService = inject(ToastService);
  authService = inject(AuthService);

  workflows = signal<WorkflowResponse[]>([]);
  allUsers = signal<UserProfileResponse[]>([]);

  showCreateWfModal = signal<boolean>(false);
  showAddStageModal = signal<boolean>(false);
  showAssignModal = signal<boolean>(false);

  newWf: CreateWorkflowRequest = { name: '', code: '' };
  selectedWfUid = '';

  newStage: CreateWorkflowStageRequest = {
    workflowUid: '',
    name: '',
    stageOrder: 2,
    institutionUid: 'inst-mof-0002'
  };

  selectedStage = signal<WorkflowStageResponse | null>(null);
  assignUserUid = '';

  ngOnInit() {
    this.loadWorkflows();
    this.userService.getAllUsers().subscribe(res => {
      if (res.success && res.data) this.allUsers.set(res.data);
    });
  }

  loadWorkflows() {
    this.workflowService.getAllWorkflows().subscribe(res => {
      if (res.success && res.data) this.workflows.set(res.data);
    });
  }

  onCreateWorkflow() {
    this.workflowService.createWorkflow(this.newWf).subscribe(res => {
      if (res.success) {
        this.toastService.success(res.message);
        this.showCreateWfModal.set(false);
        this.loadWorkflows();
      }
    });
  }

  openAddStageModal(wfUid: string) {
    this.selectedWfUid = wfUid;
    this.newStage.workflowUid = wfUid;
    this.showAddStageModal.set(true);
  }

  onCreateStage() {
    this.workflowService.createWorkflowStage(this.newStage).subscribe(res => {
      if (res.success) {
        this.toastService.success(res.message);
        this.showAddStageModal.set(false);
        this.loadWorkflows();
      }
    });
  }

  openAssignUserModal(stage: WorkflowStageResponse) {
    this.selectedStage.set(stage);
    if (this.allUsers().length > 0) {
      this.assignUserUid = this.allUsers()[0].uid;
    }
    this.showAssignModal.set(true);
  }

  onAssignUser() {
    if (!this.selectedStage()) return;
    this.workflowService.assignUserToStage({
      workflowStageUid: this.selectedStage()!.uid,
      userUid: this.assignUserUid
    }).subscribe(res => {
      if (res.success) {
        this.toastService.success(res.message);
        this.showAssignModal.set(false);
        this.loadWorkflows();
      }
    });
  }
}
